import { Box, Button, FormControl, FormControlLabel, IconButton, InputLabel, MenuItem, Select, Stack, Switch, TextField, Tooltip, Typography } from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { Interval, SupportedProviders } from '@sr/dto';
import { EntitiesWithContacts } from 'features/contact/contacts.types';
import { useContacts } from 'features/contact/contacts.hooks';
import { useEffect, useState } from 'react';
import { extractNestJsErrorMessage } from 'shared/api/rtk-query';
import { CardPaperTitled } from 'shared/ui/card-paper';
import { ErrorBanner } from 'shared/ui/error-banner';
import { useSnack } from 'shared/ui/snack';
import { generatePassword } from 'utils/generate-auth-helper';
import { formatPhone } from 'utils/phone-format-helpers';
import { userIntegrationApi } from './api';
import useConfirmDialog from 'hooks/confirm-dialog.hook';

export const UserIntegrations = ({ userId }: { userId: number }) => {
  const [domRuUserEmail, setDomRuUserEmail] = useState('');
  const [domRuPassword, setDomRuPassword] = useState('');
  const [skipAtsLeads, setSkipAtsLeads] = useState(false);
  const [mailEmail, setMailEmail] = useState('');
  const [mailPassword, setMailPassword] = useState('');
  const [mailProvider, setMailProvider] = useState(SupportedProviders.YANDEX);
  const [mailCheckInterval, setMailCheckInterval] = useState(Interval.ONE_HOUR);
  const [isMailActive, setIsMailActive] = useState(false);
  const [isChangingMailPassword, setIsChangingMailPassword] = useState(false);
  const [wazzupContactLinkId, setWazzupContactLinkId] = useState<number | ''>('');
  const { showSuccess, showError } = useSnack();
  const showConfirmDialog = useConfirmDialog();
  const { contacts, error: contactsError, isLoading: areContactsLoading } = useContacts({
    relationId: userId,
    relationType: EntitiesWithContacts.user,
  });
  const domRuQuery = userIntegrationApi.useGetUserTelephonyQuery(userId);
  const wazzupQuery = userIntegrationApi.useGetWazzupPhoneQuery(userId);
  const mailQuery = userIntegrationApi.useGetUserMailQuery(userId);
  const [createUserTelephony, { isLoading: isCreatingTelephony }] = userIntegrationApi.useCreateUserTelephonyMutation();
  const [updateUserTelephonySettings, { isLoading: isUpdatingTelephonySettings }] = userIntegrationApi.useUpdateUserTelephonySettingsMutation();
  const [deleteUserTelephony, { isLoading: isDeletingTelephony }] = userIntegrationApi.useDeleteUserTelephonyMutation();
  const [activateWazzupPhone, { isLoading: isActivatingWazzup }] = userIntegrationApi.useActivateWazzupPhoneMutation();
  const [deactivateWazzupPhone, { isLoading: isDeactivatingWazzup }] = userIntegrationApi.useDeactivateWazzupPhoneMutation();
  const [createUserMail, { isLoading: isCreatingMail }] = userIntegrationApi.useCreateUserMailMutation();
  const [changeUserMailPassword, { isLoading: isChangingMailPasswordRequest }] = userIntegrationApi.useChangeUserMailPasswordMutation();
  const [updateUserMailSettings, { isLoading: isUpdatingMailSettings }] = userIntegrationApi.useUpdateUserMailSettingsMutation();
  const [deleteUserMail, { isLoading: isDeletingMail }] = userIntegrationApi.useDeleteUserMailMutation();

  useEffect(() => {
    setDomRuUserEmail(domRuQuery.data?.login ?? '');
    setDomRuPassword(domRuQuery.data?.password ?? '');
    setSkipAtsLeads(domRuQuery.data?.skipAtsLeads ?? false);
  }, [domRuQuery.data]);

  useEffect(() => {
    setWazzupContactLinkId(wazzupQuery.data?.contactLinkId ?? '');
  }, [wazzupQuery.data?.contactLinkId]);

  useEffect(() => {
    if (!mailQuery.data) return;
    setMailEmail(mailQuery.data.email);
    setMailPassword(mailQuery.data.password);
    setMailProvider(mailQuery.data.provider);
    setMailCheckInterval(mailQuery.data.checkInterval);
    setIsMailActive(mailQuery.data.active);
  }, [mailQuery.data]);

  useEffect(() => {
    setIsChangingMailPassword(false);
  }, [userId]);

  const handleCreateTelephony = async () => {
    if (!domRuUserEmail.trim() || !domRuPassword) {
      showError('Заполните логин и пароль');
      return;
    }

    try {
      const result = await createUserTelephony({
        userId,
        login: domRuUserEmail.trim(),
        password: domRuPassword,
      }).unwrap();
      setDomRuUserEmail(result.login);
      setDomRuPassword(result.password);
      showSuccess('Телефон создан');
    } catch (e) {
      showError('Не удалось создать телефон', e);
    }
  };

  const handleSaveTelephonySettings = async () => {
    try {
      await updateUserTelephonySettings({ userId, skipAtsLeads }).unwrap();
      showSuccess('Настройки телефонии сохранены');
    } catch (e) {
      showError('Не удалось сохранить настройки телефонии', e);
    }
  };

  const handleDeleteTelephony = async () => {
    await showConfirmDialog({
      title: 'Удалить телефон?',
      content: `Пользователь ${domRuUserEmail} будет удалён из виртуальной АТС Дом.ру. Продолжить?`,
    });

    try {
      const result = await deleteUserTelephony(userId).unwrap();
      setDomRuUserEmail(result.login);
      setDomRuPassword(result.password);
      showSuccess('Телефон удалён');
    } catch (e) {
      showError('Не удалось удалить телефон', e);
    }
  };

  const handleActivateWazzup = async () => {
    if (!wazzupContactLinkId) {
      showError('Выберите номер телефона');
      return;
    }

    try {
      const result = await activateWazzupPhone({ userId, contactLinkId: wazzupContactLinkId }).unwrap();
      setWazzupContactLinkId(result.contactLinkId ?? '');
      showSuccess('Телефон подключён к Wazzup');
    } catch (e) {
      showError('Не удалось подключить телефон к Wazzup', e);
    }
  };

  const handleDeactivateWazzup = async () => {
    try {
      await deactivateWazzupPhone(userId).unwrap();
      setWazzupContactLinkId('');
      showSuccess('Телефон удалён из Wazzup');
    } catch (e) {
      showError('Не удалось удалить телефон из Wazzup', e);
    }
  };

  const handleCreateMail = async () => {
    if (!mailEmail.trim() || !mailPassword) {
      showError('Заполните адрес почты и пароль');
      return;
    }

    try {
      const result = await createUserMail({
        userId,
        email: mailEmail.trim(),
        password: mailPassword,
        provider: mailProvider,
        checkInterval: mailCheckInterval,
        active: isMailActive,
      }).unwrap();
      setMailEmail(result.email);
      setMailPassword(result.password);
      showSuccess('Почтовый ящик создан');
    } catch (e) {
      showError('Не удалось создать почтовый ящик', e);
    }
  };

  const handleChangeMailPassword = async () => {
    if (!mailPassword) {
      showError('Заполните новый пароль');
      return;
    }

    try {
      const result = await changeUserMailPassword({ userId, password: mailPassword }).unwrap();
      setMailPassword(result.password);
      setIsChangingMailPassword(false);
      showSuccess('Пароль почтового ящика изменён');
    } catch (e) {
      showError('Не удалось изменить пароль почтового ящика', e);
    }
  };

  const handleCancelMailPasswordChange = () => {
    setMailPassword(mailQuery.data?.password ?? '');
    setIsChangingMailPassword(false);
  };

  const handleSaveMailSettings = async () => {
    try {
      await updateUserMailSettings({
        userId,
        settings: {
          provider: mailProvider,
          checkInterval: mailCheckInterval,
          active: isMailActive,
        },
      }).unwrap();
      showSuccess('Настройки почтового ящика сохранены');
    } catch (e) {
      showError('Не удалось сохранить настройки почтового ящика', e);
    }
  };

  const handleDeleteMail = async () => {
    await showConfirmDialog({
      title: 'Удалить почтовый ящик?',
      content: `Ящик ${mailEmail} будет безвозвратно удалён из Яндекс 360. Письма, уже скачанные в приложение, сохранятся. Продолжить?`,
    });

    try {
      const result = await deleteUserMail(userId).unwrap();
      setIsMailActive(false);
      setIsChangingMailPassword(false);
      setMailPassword(result.password);
      showSuccess('Почтовый ящик удалён из Яндекс 360');
    } catch (e) {
      showError('Не удалось удалить почтовый ящик', e);
    }
  };

  const isMailSaving = isCreatingMail || isChangingMailPasswordRequest || isUpdatingMailSettings || isDeletingMail;
  const isMailDeleted = !!mailQuery.data?.deleted;

  const isWazzupSaving = isActivatingWazzup || isDeactivatingWazzup;
  const isTelephonySaving = isCreatingTelephony || isDeletingTelephony || isUpdatingTelephonySettings;
  const userPhones = contacts?.linkedPhones ?? [];

  return (
    <Stack spacing={2}>
      <CardPaperTitled title="Почта">
        <Stack spacing={2} sx={{ maxWidth: 700 }}>
          {mailQuery.error && <ErrorBanner errorMessage={extractNestJsErrorMessage(mailQuery.error)} />}
          {isMailDeleted && (
            <Typography color="error.main">
              Почтовый ящик удалён из Яндекс 360. Скачанные письма сохранены в приложении.
            </Typography>
          )}
          <TextField
            label="Email"
            type="email"
            value={mailEmail}
            onChange={(e) => setMailEmail(e.target.value)}
            fullWidth
            disabled={mailQuery.isLoading || isMailSaving || mailQuery.data?.exists || isMailDeleted}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              label="Пароль"
              value={mailPassword}
              onChange={(e) => setMailPassword(e.target.value)}
              fullWidth
              disabled={mailQuery.isLoading || isMailSaving || isMailDeleted || (mailQuery.data?.exists && !isChangingMailPassword)}
            />
            {(!mailQuery.data?.exists || isChangingMailPassword) && (
              <Tooltip title="Сгенерировать пароль">
                <span>
                  <IconButton
                    color="primary"
                    aria-label="generate-password"
                    onClick={() => setMailPassword(generatePassword())}
                    disabled={mailQuery.isLoading || isMailSaving}
                  >
                    <AutoFixHighIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Box>
          <FormControl fullWidth disabled={mailQuery.isLoading || isMailSaving || isMailDeleted}>
            <InputLabel id="mail-provider-label">Провайдер</InputLabel>
            <Select
              labelId="mail-provider-label"
              label="Провайдер"
              value={mailProvider}
              onChange={(e) => setMailProvider(e.target.value as SupportedProviders)}
            >
              <MenuItem value={SupportedProviders.YANDEX}>YANDEX</MenuItem>
              <MenuItem value={SupportedProviders.GOOGLE} disabled>GOOGLE</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth disabled={mailQuery.isLoading || isMailSaving || isMailDeleted}>
            <InputLabel id="mail-check-interval-label">Интервал опроса</InputLabel>
            <Select
              labelId="mail-check-interval-label"
              label="Интервал опроса"
              value={mailCheckInterval}
              onChange={(e) => setMailCheckInterval(e.target.value as Interval)}
            >
              <MenuItem value={Interval.ONE_MINUTE}>Минута</MenuItem>
              <MenuItem value={Interval.TEN_MINUTES}>10 минут</MenuItem>
              <MenuItem value={Interval.THIRTY_MINUTES}>30 минут</MenuItem>
              <MenuItem value={Interval.ONE_HOUR}>Час</MenuItem>
              <MenuItem value={Interval.ONE_DAY}>День</MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel
            control={<Switch checked={isMailActive} onChange={(e) => setIsMailActive(e.target.checked)} />}
            label="Активна"
            disabled={mailQuery.isLoading || isMailSaving || isMailDeleted}
          />
          {mailQuery.data && !mailQuery.data.exists && (
            <Button variant="contained" onClick={handleCreateMail} disabled={isMailSaving || !mailEmail.trim() || !mailPassword}>
              Создать
            </Button>
          )}
          {mailQuery.data?.exists && !isMailDeleted && !isChangingMailPassword && (
            <Stack direction="row" spacing={2}>
              <Button variant="contained" onClick={handleSaveMailSettings} disabled={isMailSaving}>
                Сохранить настройки
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setMailPassword('');
                  setIsChangingMailPassword(true);
                }}
                disabled={isMailSaving}
              >
                Изменить пароль
              </Button>
              <Button color="error" variant="outlined" onClick={handleDeleteMail} disabled={isMailSaving}>
                Удалить почтовый ящик
              </Button>
            </Stack>
          )}
          {mailQuery.data?.exists && !isMailDeleted && isChangingMailPassword && (
            <Stack direction="row" spacing={2}>
              <Button variant="contained" onClick={handleChangeMailPassword} disabled={isChangingMailPasswordRequest || !mailPassword}>
                Сохранить
              </Button>
              <Button variant="outlined" onClick={handleCancelMailPasswordChange} disabled={isChangingMailPasswordRequest}>
                Отмена
              </Button>
            </Stack>
          )}
        </Stack>
      </CardPaperTitled>

      <CardPaperTitled title="Телефония">
        <Stack spacing={2} sx={{ maxWidth: 700 }}>
          {domRuQuery.error && <ErrorBanner errorMessage={extractNestJsErrorMessage(domRuQuery.error)} />}
          <TextField
            label="Логин"
            value={domRuUserEmail}
            onChange={(e) => setDomRuUserEmail(e.target.value)}
            placeholder="id@vpbx038304385.domru.biz"
            fullWidth
            disabled={domRuQuery.isLoading || isTelephonySaving || domRuQuery.data?.exists}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              label="Пароль"
              value={domRuPassword}
              onChange={(e) => setDomRuPassword(e.target.value)}
              fullWidth
              disabled={domRuQuery.isLoading || isTelephonySaving || domRuQuery.data?.exists}
            />
            {!domRuQuery.data?.exists && (
              <Tooltip title="Сгенерировать пароль">
                <span>
                  <IconButton
                    color="primary"
                    aria-label="generate-telephony-password"
                    onClick={() => setDomRuPassword(generatePassword())}
                    disabled={domRuQuery.isLoading || isTelephonySaving}
                  >
                    <AutoFixHighIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Box>
          <FormControlLabel
            control={<Switch checked={skipAtsLeads} onChange={(e) => setSkipAtsLeads(e.target.checked)} />}
            label="Не создавать лиды из звонков"
            disabled={!domRuQuery.currentData || domRuQuery.isFetching || isTelephonySaving}
          />
          <Typography color="text.secondary" variant="body2">
            Лиды из входящих, исходящих и пропущенных звонков этого сотрудника не создаются. История звонков сохраняется.
          </Typography>
          <Button
            variant="contained"
            onClick={handleSaveTelephonySettings}
            disabled={!domRuQuery.currentData || domRuQuery.isFetching || isTelephonySaving}
          >
            Сохранить настройки
          </Button>
          {domRuQuery.data && !domRuQuery.data.exists && (
            <Button
              variant="contained"
              onClick={handleCreateTelephony}
              disabled={isTelephonySaving || !domRuUserEmail.trim() || !domRuPassword}
            >
              Создать
            </Button>
          )}
          {domRuQuery.data?.exists && (
            <Button color="error" variant="outlined" onClick={handleDeleteTelephony} disabled={isTelephonySaving}>
              Удалить телефон
            </Button>
          )}
        </Stack>
      </CardPaperTitled>

      <CardPaperTitled title="Wazzup">
        <Stack spacing={2} sx={{ maxWidth: 700 }}>
          <Typography color="text.secondary">
            Выберите мобильный телефон из контактов пользователя. Номер будет зарегистрирован в Wazzup и позволит менеджеру использовать мобильное приложение.
          </Typography>
          {wazzupQuery.error && <ErrorBanner errorMessage={extractNestJsErrorMessage(wazzupQuery.error)} />}
          {contactsError && <ErrorBanner errorMessage={contactsError} />}
          <FormControl fullWidth disabled={wazzupQuery.isLoading || areContactsLoading || isWazzupSaving || userPhones.length === 0}>
            <InputLabel id="wazzup-phone-label">Телефон</InputLabel>
            <Select
              labelId="wazzup-phone-label"
              label="Телефон"
              value={wazzupContactLinkId}
              onChange={(e) => setWazzupContactLinkId(e.target.value === '' ? '' : Number(e.target.value))}
            >
              <MenuItem value="" disabled>
                Выберите телефон
              </MenuItem>
              {userPhones.map((phone) => (
                <MenuItem key={phone.id} value={phone.id}>
                  {formatPhone(phone.phone)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {userPhones.length === 0 && !areContactsLoading && (
            <Typography color="text.secondary">Сначала добавьте телефон в контактах пользователя на вкладке «Основное».</Typography>
          )}
          {wazzupQuery.data && wazzupQuery.data.contactLinkId === null && (
            <Typography color="warning.main">
              Подключённый номер {formatPhone(wazzupQuery.data.phone)} больше не привязан к пользователю. Выберите другой номер или удалите настройку.
            </Typography>
          )}
          <Stack direction="row" spacing={2}>
            <Button variant="contained" onClick={handleActivateWazzup} disabled={wazzupQuery.isLoading || areContactsLoading || isWazzupSaving || !wazzupContactLinkId}>
              {wazzupQuery.data ? 'Обновить' : 'Подключить'}
            </Button>
            {wazzupQuery.data && (
              <Button color="error" variant="outlined" onClick={handleDeactivateWazzup} disabled={isWazzupSaving}>
                Удалить телефон
              </Button>
            )}
          </Stack>
        </Stack>
      </CardPaperTitled>
    </Stack>
  );
};
